import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  X,
} from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface DatePickerProps {
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Hint text shown below input */
  hint?: string;
  /** Currently selected date (ISO string or Date) */
  value?: string | Date | null;
  /** Callback when date changes */
  onChange?: (date: string | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Minimum selectable date */
  minDate?: Date | string;
  /** Maximum selectable date */
  maxDate?: Date | string;
  /** Date format for display (default: 'MM/DD/YYYY') */
  dateFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  /** Include time picker */
  showTimePicker?: boolean;
  /** Time format (12h or 24h) */
  timeFormat?: '12h' | '24h';
  /** Disable the input */
  disabled?: boolean;
  /** Required field */
  required?: boolean;
  /** Show clear button */
  showClear?: boolean;
  /** Show today button */
  showToday?: boolean;
  /** First day of week (0 = Sunday, 1 = Monday) */
  firstDayOfWeek?: 0 | 1;
  /** Custom class name */
  className?: string;
  /** Input ID */
  id?: string;
}

export interface DatePickerRef {
  focus: () => void;
  blur: () => void;
  clear: () => void;
}

const DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DatePicker = forwardRef<DatePickerRef, DatePickerProps>(
  (
    {
      label,
      error,
      hint,
      value,
      onChange,
      placeholder = 'Select date',
      minDate,
      maxDate,
      dateFormat = 'MM/DD/YYYY',
      showTimePicker = false,
      timeFormat = '12h',
      disabled = false,
      required = false,
      showClear = true,
      showToday = true,
      firstDayOfWeek = 0,
      className = '',
      id,
    },
    ref
  ) => {
    const inputId = id || `datepicker-${Math.random().toString(36).substr(2, 9)}`;
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // State
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState({ hours: 12, minutes: 0 });
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [showYearPicker, setShowYearPicker] = useState(false);
    const [focusedDate, setFocusedDate] = useState<Date | null>(null);

    // Parse value prop
    useEffect(() => {
      if (value) {
        const date = typeof value === 'string' ? new Date(value) : value;
        if (!isNaN(date.getTime())) {
          setSelectedDate(date);
          setViewDate(date);
          if (showTimePicker) {
            setSelectedTime({
              hours: date.getHours(),
              minutes: date.getMinutes(),
            });
          }
        }
      } else {
        setSelectedDate(null);
      }
    }, [value, showTimePicker]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      clear: () => handleClear(),
    }));

    // Handle click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setShowMonthPicker(false);
          setShowYearPicker(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check if date is disabled
    const isDateDisabled = useCallback(
      (date: Date) => {
        const parsedMin = minDate
          ? typeof minDate === 'string'
            ? new Date(minDate)
            : minDate
          : null;
        const parsedMax = maxDate
          ? typeof maxDate === 'string'
            ? new Date(maxDate)
            : maxDate
          : null;
        if (parsedMin && date < parsedMin) return true;
        if (parsedMax && date > parsedMax) return true;
        return false;
      },
      [minDate, maxDate]
    );

    // Format date for display
    const formatDate = useCallback(
      (date: Date | null): string => {
        if (!date) return '';

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        let formatted = '';
        switch (dateFormat) {
          case 'DD/MM/YYYY':
            formatted = `${day}/${month}/${year}`;
            break;
          case 'YYYY-MM-DD':
            formatted = `${year}-${month}-${day}`;
            break;
          default:
            formatted = `${month}/${day}/${year}`;
        }

        if (showTimePicker) {
          const hours = selectedTime.hours;
          const minutes = selectedTime.minutes.toString().padStart(2, '0');
          if (timeFormat === '12h') {
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            formatted += ` ${displayHours}:${minutes} ${period}`;
          } else {
            formatted += ` ${hours.toString().padStart(2, '0')}:${minutes}`;
          }
        }

        return formatted;
      },
      [dateFormat, showTimePicker, timeFormat, selectedTime]
    );

    // Get calendar days
    const getCalendarDays = useCallback(() => {
      const year = viewDate.getFullYear();
      const month = viewDate.getMonth();

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const startOffset = (firstDay.getDay() - firstDayOfWeek + 7) % 7;
      const days: (Date | null)[] = [];

      // Add empty cells for days before first day of month
      for (let i = 0; i < startOffset; i++) {
        const prevDate = new Date(year, month, -startOffset + i + 1);
        days.push(prevDate);
      }

      // Add days of the month
      for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push(new Date(year, month, i));
      }

      // Add empty cells for days after last day of month
      const remaining = 42 - days.length;
      for (let i = 1; i <= remaining; i++) {
        const nextDate = new Date(year, month + 1, i);
        days.push(nextDate);
      }

      return days;
    }, [viewDate, firstDayOfWeek]);

    // Check if date is today
    const isToday = (date: Date) => {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    };

    // Check if date is selected
    const isSelected = (date: Date) => {
      if (!selectedDate) return false;
      return (
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear()
      );
    };

    // Check if date is in current month
    const isCurrentMonth = (date: Date) => {
      return date.getMonth() === viewDate.getMonth();
    };

    // Handle date selection
    const handleSelectDate = (date: Date) => {
      if (isDateDisabled(date)) return;

      setSelectedDate(date);
      setFocusedDate(date);

      // Create ISO string with time if applicable
      let isoString: string;
      if (showTimePicker) {
        const newDate = new Date(date);
        newDate.setHours(selectedTime.hours, selectedTime.minutes, 0, 0);
        isoString = newDate.toISOString();
      } else {
        isoString = date.toISOString().split('T')[0];
      }

      onChange?.(isoString);

      if (!showTimePicker) {
        setIsOpen(false);
      }
    };

    // Handle time change
    const handleTimeChange = (type: 'hours' | 'minutes', value: number) => {
      const newTime = { ...selectedTime, [type]: value };
      setSelectedTime(newTime);

      if (selectedDate) {
        const newDate = new Date(selectedDate);
        newDate.setHours(newTime.hours, newTime.minutes, 0, 0);
        onChange?.(newDate.toISOString());
      }
    };

    // Navigation handlers
    const handlePrevMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const handlePrevYear = () => {
      setViewDate(new Date(viewDate.getFullYear() - 1, viewDate.getMonth(), 1));
    };

    const handleNextYear = () => {
      setViewDate(new Date(viewDate.getFullYear() + 1, viewDate.getMonth(), 1));
    };

    // Handle clear
    const handleClear = () => {
      setSelectedDate(null);
      setSelectedTime({ hours: 12, minutes: 0 });
      onChange?.(null);
    };

    // Handle today
    const handleToday = () => {
      const today = new Date();
      if (!isDateDisabled(today)) {
        handleSelectDate(today);
        setViewDate(today);
      }
    };

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setIsOpen(true);
          setFocusedDate(selectedDate || new Date());
        }
        return;
      }

      const currentFocus = focusedDate || selectedDate || new Date();

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setFocusedDate(new Date(currentFocus.getTime() - 86400000));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setFocusedDate(new Date(currentFocus.getTime() + 86400000));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedDate(new Date(currentFocus.getTime() - 7 * 86400000));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedDate(new Date(currentFocus.getTime() + 7 * 86400000));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedDate) {
            handleSelectDate(focusedDate);
          }
          break;
      }
    };

    // Adjust days display for first day of week
    const displayDays = firstDayOfWeek === 1 ? [...DAYS_SHORT.slice(1), DAYS_SHORT[0]] : DAYS_SHORT;

    // Generate year options for picker
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 20 }, (_, i) => currentYear - 10 + i);

    return (
      <div className={`w-full font-['Inter',sans-serif] ${className}`} ref={containerRef}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-base font-medium text-[#1F1F1F] mb-2 leading-[1.75em]"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Input */}
        <div className="relative">
          <div
            className={`flex items-center rounded-lg border transition-all duration-200 ${
              error
                ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500'
                : disabled
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-gray-300 hover:border-[var(--brand-primary)] focus-within:ring-2 focus-within:ring-[var(--brand-primary)] focus-within:border-[var(--brand-primary)] focus-within:ring-opacity-20'
            }`}
          >
            <div className="flex items-center pl-3">
              <Calendar className="h-4 w-4 text-gray-500" />
            </div>

            <input
              ref={inputRef}
              id={inputId}
              type="text"
              readOnly
              value={formatDate(selectedDate)}
              placeholder={placeholder}
              disabled={disabled}
              onClick={() => !disabled && setIsOpen(true)}
              onKeyDown={handleKeyDown}
              className={`w-full px-3 py-2.5 outline-none text-sm font-normal leading-[1.75em] placeholder-[#6E7077] rounded-lg cursor-pointer ${
                disabled
                  ? 'bg-gray-50 text-[#6E7077] cursor-not-allowed'
                  : 'bg-white text-[#1F1F1F]'
              }`}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
            />

            {/* Clear button */}
            {showClear && selectedDate && !disabled && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 mr-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Clear date"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            <div className="flex items-center pr-3">
              <ChevronDown
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>

          {/* Calendar Dropdown */}
          {isOpen && (
            <div
              className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[280px]"
              role="dialog"
              aria-label="Date picker"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-3 border-b border-gray-100">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600" />
                </button>

                <div className="flex items-center space-x-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMonthPicker(!showMonthPicker);
                      setShowYearPicker(false);
                    }}
                    className="px-2 py-1 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded transition-colors"
                  >
                    {MONTHS[viewDate.getMonth()]}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowYearPicker(!showYearPicker);
                      setShowMonthPicker(false);
                    }}
                    className="px-2 py-1 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded transition-colors"
                  >
                    {viewDate.getFullYear()}
                  </button>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>

                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              {/* Month Picker */}
              {showMonthPicker && (
                <div className="p-3 grid grid-cols-3 gap-2">
                  {MONTHS.map((month, index) => (
                    <button
                      key={month}
                      type="button"
                      onClick={() => {
                        setViewDate(new Date(viewDate.getFullYear(), index, 1));
                        setShowMonthPicker(false);
                      }}
                      className={`px-2 py-1.5 text-sm rounded transition-colors ${
                        index === viewDate.getMonth()
                          ? 'bg-[var(--brand-primary)] text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {month.slice(0, 3)}
                    </button>
                  ))}
                </div>
              )}

              {/* Year Picker */}
              {showYearPicker && (
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={handlePrevYear}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-medium text-gray-700">Select Year</span>
                    <button
                      type="button"
                      onClick={handleNextYear}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {yearOptions.map(year => (
                      <button
                        key={year}
                        type="button"
                        onClick={() => {
                          setViewDate(new Date(year, viewDate.getMonth(), 1));
                          setShowYearPicker(false);
                        }}
                        className={`px-2 py-1.5 text-sm rounded transition-colors ${
                          year === viewDate.getFullYear()
                            ? 'bg-[var(--brand-primary)] text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Calendar Grid */}
              {!showMonthPicker && !showYearPicker && (
                <div className="p-3">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {displayDays.map((day, index) => (
                      <div
                        key={index}
                        className="text-center text-xs font-medium text-gray-500 py-1"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {getCalendarDays().map((date, index) => {
                      if (!date) return <div key={index} />;

                      const dateDisabled = isDateDisabled(date);
                      const selected = isSelected(date);
                      const today = isToday(date);
                      const currentMonth = isCurrentMonth(date);
                      const focused =
                        focusedDate &&
                        date.getDate() === focusedDate.getDate() &&
                        date.getMonth() === focusedDate.getMonth() &&
                        date.getFullYear() === focusedDate.getFullYear();

                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSelectDate(date)}
                          disabled={dateDisabled}
                          className={`
                            w-9 h-9 text-sm rounded-lg transition-all duration-150 flex items-center justify-center
                            ${dateDisabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                            ${!currentMonth && !selected ? 'text-gray-400' : ''}
                            ${
                              selected
                                ? 'bg-[var(--brand-primary)] text-white font-medium'
                                : today
                                  ? 'border border-[var(--brand-primary)] text-[var(--brand-primary)] font-medium'
                                  : currentMonth
                                    ? 'text-gray-700 hover:bg-brand-50'
                                    : 'hover:bg-gray-100'
                            }
                            ${focused && !selected ? 'ring-2 ring-[var(--brand-primary)] ring-opacity-50' : ''}
                          `}
                          aria-label={date.toLocaleDateString()}
                          aria-selected={selected}
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Time Picker */}
              {showTimePicker && !showMonthPicker && !showYearPicker && (
                <div className="p-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <select
                      value={selectedTime.hours}
                      onChange={e => handleTimeChange('hours', parseInt(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {timeFormat === '12h'
                            ? `${i % 12 || 12} ${i >= 12 ? 'PM' : 'AM'}`
                            : i.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <span className="text-gray-500">:</span>
                    <select
                      value={selectedTime.minutes}
                      onChange={e => handleTimeChange('minutes', parseInt(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                    >
                      {Array.from({ length: 60 }, (_, i) => (
                        <option key={i} value={i}>
                          {i.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex items-center justify-between p-3 border-t border-gray-100">
                {showClear && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-sm text-gray-600 hover:text-[var(--brand-primary)] transition-colors font-medium"
                  >
                    Clear
                  </button>
                )}
                {showToday && (
                  <button
                    type="button"
                    onClick={handleToday}
                    className="text-sm text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)] transition-colors font-medium"
                  >
                    Today
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="mt-2 text-sm text-red-600 font-normal leading-[1.75em] flex items-center gap-1">
            <AlertCircle size={14} className="flex-shrink-0" />
            {error}
          </p>
        )}

        {/* Hint Text */}
        {hint && !error && (
          <p className="mt-2 text-sm text-[#6E7077] font-normal leading-[1.75em]">{hint}</p>
        )}
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
